// === Pure Utility Functions ===
// No dependencies on app state, DOM, or other modules.

/** UUID v4，兼容 HTTP（非 HTTPS 下 crypto.randomUUID 不可用） */
export function generateUUID(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  // Fallback via getRandomValues（HTTP / 非安全上下文）
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/** 深拷贝，优先 structuredClone，降级 JSON */
export function deepClone<T>(value: T): T {
  if (value === null || value === undefined) return value;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

/** 字节数 → 可读字符串（B / KB / MB） */
export function formatFileSize(bytes: number): string {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
  return `${(size / (1024 * 1024)).toFixed(1)}MB`;
}

/** HTML 实体转义，防止 XSS */
export function escapeHtml(str: unknown): string {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface NormalizeSafeHrefOptions {
  externalOnly?: boolean;
  allowRelative?: boolean;
  allowDataImage?: boolean;
}

/** URL 安全化：过滤 javascript:/vbscript:/data: 等危险协议 */
export function normalizeSafeHref(rawHref: unknown, options: NormalizeSafeHrefOptions = {}): string {
  const href = String(rawHref || '').trim();
  if (!href) return '';
  const externalOnly = options.externalOnly === true;
  const allowRelative = options.allowRelative !== false;
  const allowDataImage = options.allowDataImage === true;
  const lower = href.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) return '';
  if (lower.startsWith('data:')) {
    if (allowDataImage && /^data:image\/[a-z0-9.+-]+;base64,/i.test(href)) return href;
    return '';
  }
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(href)) {
    try {
      const parsed = new URL(href);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.toString();
      if (!externalOnly && (parsed.protocol === 'mailto:' || parsed.protocol === 'tel:')) return parsed.toString();
      return '';
    } catch {
      return '';
    }
  }
  if (externalOnly || !allowRelative || href.startsWith('//')) return '';
  if (/^(#|\/|\.\.?\/|\?)/.test(href)) return href;
  return '';
}

interface UpdateInfo {
  latestVersion?: string;
  localVersion?: string;
  releaseUrl?: string;
}

/** 构建版本更新提示 HTML */
export function buildUpdateStatusHtml(info: UpdateInfo): string {
  const latestVersion = escapeHtml(info?.latestVersion || '');
  const localVersion = escapeHtml(info?.localVersion || '');
  const safeReleaseUrl = normalizeSafeHref(info?.releaseUrl, { externalOnly: true, allowRelative: false });
  if (!safeReleaseUrl) {
    return `有新版本 <strong>v${latestVersion}</strong>（当前 v${localVersion}）`;
  }
  return `有新版本 <strong>v${latestVersion}</strong>（当前 v${localVersion}）&nbsp;<a href="${escapeHtml(safeReleaseUrl)}" target="_blank" rel="noopener noreferrer" style="color:var(--accent)">查看更新</a>`;
}

/** 相对时间（中文，可选紧凑英文单位） */
export function timeAgo(dateStr: string, options: { showSeconds?: boolean; compact?: boolean } = {}): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const timestamp = date.getTime();
  if (Number.isNaN(timestamp)) return '';
  const diff = Date.now() - timestamp;
  const seconds = Math.max(0, Math.floor(diff / 1000));
  if (options.compact) {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d`;
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
  }
  if (seconds < 60) return options.showSeconds ? `${seconds}秒前` : '刚刚';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}天前`;
  return date.toLocaleDateString('zh-CN');
}
